<template>
  <div class="ebank-page">
    <div class="ebank-container">
      <!-- 顶部：金额 + 关闭 -->
      <div class="header">
        <div class="title">网银支付 - {{ isB2B ? '企业网银 (B2B)' : '个人网银 (B2C)' }}</div>
        <div class="amount">支付金额 <span>￥{{ amount }}</span></div>
        <div class="order-info">订单：{{ mchOrderNo }}</div>
      </div>

      <!-- 银行选择（下拉弹层） -->
      <div class="form-card bank-select-card">
        <div
          class="form-row clickable"
          @click="openBankSheet"
        >
          <div class="label">付款银行</div>
          <div class="field">
            <span :class="{ placeholder: !selectedBankName }">
              {{ selectedBankName || '请选择银行' }}
            </span>
            <span class="arrow">›</span>
          </div>
        </div>
      </div>

      <!-- 提示 -->
      <div class="tips">
        <p>选择银行后点击"下一步"跳转到中金收银台完成支付。</p>
      </div>

      <!-- 操作按钮 -->
      <div class="action">
        <button class="pay-btn" :disabled="!canSubmit || payBtnDisabled" @click="next">
          {{ payBtnText }}
        </button>
        <button class="cancel-btn" @click="cancel">取消</button>
      </div>
    </div>

    <!-- 银行选择弹层 -->
    <div class="mask" :class="{ show: showBankSheet }" @click="closeBankSheet"></div>
    <div class="sheet" :class="{ show: showBankSheet }">
      <div class="sheet-title">
        选择付款银行
        <div class="sheet-close" @click="closeBankSheet">×</div>
      </div>
      <input
        v-model="bankKeyword"
        class="bank-search"
        type="search"
        inputmode="search"
        enterkeyhint="search"
        placeholder="搜索银行"
      />
      <div class="bank-list">
        <div
          v-for="item in filteredBankList"
          :key="item.bank_id"
          class="bank-item"
          :class="{ active: selectedBankId === item.bank_id }"
          @click="selectBank(item)"
        >
          <div class="bank-logo">{{ getBankShort(item.bank_name) }}</div>
          <div class="bank-name">{{ item.bank_name }}</div>
        </div>
        <div v-if="filteredBankList.length === 0" class="bank-empty">未找到匹配的银行</div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/http/request'
import bankIdList from './bank-id.json'

// 中金网银专用：解 token 拿展示信息（不落库）
function getEbankOrderInfo (token) {
  return request.request({
    url: '/api/cashier/ebankOrderInfo',
    method: 'POST',
    data: { token }
  })
}

// 中金网银专用：解 token + channelExtra → 真正落库 + 调中金 5011，返回收银台 URL
function ebankSubmit (token, channelExtra) {
  return request.request({
    url: '/api/cashier/ebankSubmit',
    method: 'POST',
    data: { token, channelExtra }
  })
}

const payBtnText = '下一步'

export default {
  name: 'Ebank',
  // 是否启用弹层模式（true 时由父组件控制显隐，否则按路由参数独立运行）
  props: {
    visible: { type: Boolean, default: null },
    amount: { type: [String, Number], default: '' },
    subject: { type: String, default: '' },
    mchOrderNo: { type: String, default: '' },
    ifCode: { type: String, default: '' },
    token: { type: String, default: '' },
    wayCode: { type: String, default: '' }
  },
  data() {
    return {
      // 弹层模式时使用 props.visible，页面模式时固定 true
      internalVisible: true,
      amountText: '0.00',
      orderInfo: {},
      mchOrderNoText: '',
      bankIdList,
      selectedBankId: '',
      payBtnDisabled: false,
      payBtnText: payBtnText,
      // 银行选择弹层
      showBankSheet: false,
      bankKeyword: ''
    }
  },
  computed: {
    isModalMode() {
      // 传入了 visible 即视为弹层模式
      return this.visible !== null && this.visible !== undefined
    },
    currentVisible: {
      get() {
        return this.isModalMode ? this.visible : this.internalVisible
      },
      set(val) {
        if (this.isModalMode) {
          this.$emit('update:visible', val)
        } else {
          this.internalVisible = val
        }
      }
    },
    isB2B() {
      return this.wayCodeProp === 'UP_B2B'
    },
    wayCodeProp() {
      return this.wayCode || this.orderInfo.wayCode || ''
    },
    // 是否允许提交：B2C/B2B 表单跳转模式均只需选择银行
    canSubmit() {
      return !!this.selectedBankId
    },
    filteredBankList() {
      const keyword = (this.bankKeyword || '').trim()
      if (!keyword) {
        return this.bankIdList
      }
      return this.bankIdList.filter(item => item.bank_name.indexOf(keyword) >= 0)
    },
    selectedBankName() {
      const bank = this.bankIdList.find(item => item.bank_id === this.selectedBankId)
      return bank ? bank.bank_name : ''
    }
  },
  watch: {
    // 弹层打开时重新拉订单信息
    currentVisible(val) {
      if (val && this.isModalMode) {
        this.resetState()
        this.applyProps()
      }
    },
    // 外部 token/ifCode 变化时重新初始化
    token: {
      immediate: false,
      handler() {
        if (this.isModalMode) this.applyProps()
      }
    }
  },
  mounted() {
    if (this.isModalMode) {
      this.applyProps()
    } else {
      this.init()
    }
  },
  methods: {
    // 弹层模式：从 props 同步到 data（不走 API 拉单，避免重复请求）
    applyProps() {
      this.orderToken = this.token
      this.orderInfo = { subject: this.subject }
    },
    // 弹层模式：每次重新打开时清空上一次的输入
    resetState() {
      this.selectedBankId = ''
      this.bankKeyword = ''
      this.payBtnDisabled = false
      this.payBtnText = payBtnText
    },
    getBankShort(bankName) {
      if (!bankName) {
        return '银'
      }
      const name = String(bankName).replace(/^中国/, '')
      return name.charAt(0) || '银'
    },
    openBankSheet() {
      this.showBankSheet = true
      this.bankKeyword = ''
    },
    closeBankSheet() {
      this.showBankSheet = false
    },
    selectBank(item) {
      this.selectedBankId = item.bank_id
      this.closeBankSheet()
    },
    init() {
      const { ifCode, token } = this.$router.history.current.params
      this.orderToken = token
      this.ifCode = ifCode
      if (token) {
        // 中金网银专用：解 token 拿展示信息（订单尚未落库）
        getEbankOrderInfo(token).then(info => {
          if (info) {
            this.amount = ((info.amount || 0) / 100).toFixed(2)
            this.orderInfo = { subject: info.subject }
            this.mchOrderNo = info.mchOrderNo || ''
            this.wayCode = info.wayCode || ''
          }
        }).catch(err => {
          console.error('获取订单信息失败', err)
          alert('订单参数已失效，请返回商户重新下单')
        })
      }
    },
    next() {
      if (!this.selectedBankId) {
        alert('请先选择银行')
        return
      }
      this.payBtnDisabled = true
      this.payBtnText = '提交中...'

      // 表单跳转模式：B2C / B2B 均只需上送 bankId（5011 接口 EPaymentWay=10 时对公四要素非必填）
      const channelExtra = { bankId: this.selectedBankId }

      // 中金网银专用：解 token + channelExtra → 真正落库 + 调中金 5011
      ebankSubmit(this.orderToken, JSON.stringify(channelExtra))
        .then(res => {
          // res 已是后端 ApiRes.data
          if (res && res.code !== undefined && res.code !== 0) {
            alert(`下单失败: ${res.msg || res.errMsg}`)
            this.payBtnDisabled = false
            this.payBtnText = payBtnText
            return
          }
          if (res && res.payUrl) {
            window.location.href = res.payUrl
          } else {
            alert('支付受理成功，等待银行回调')
          }
        })
        .catch(err => {
          console.error(err)
          alert(err && err.msg ? `支付失败: ${err.msg}` : '支付失败')
        })
        .finally(() => {
          this.payBtnDisabled = false
          this.payBtnText = payBtnText
        })
    },
    cancel() {
      if (window.history.length > 1) {
        window.history.back()
      } else {
        window.close()
      }
    }
  }
}
</script>

<style src="./bank_bind/ebank.css"></style>
